import type { tickets } from '@repletoryreach/sdk-api';
import { Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Checkbox, NumberInput, Select, Textarea, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../i18n/I18nProvider';

/**
 * One custom field of a ticket type, drawn as the control its type needs. Required fields carry the asterisk;
 * the value is what the service stores (numbers, booleans and lists as such).
 */
export function FieldInput({ field, value, onChange, disabled, error }: {
  field: tickets.TypeField;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
  error?: string;
}) {
  const t = useT();
  const common = { label: field.label, required: field.required, disabled, error };
  const text = value == null ? '' : String(value);

  switch (field.type) {
    case 'number':
      return (
        <NumberInput
          {...common}
          value={typeof value === 'number' ? value : text === '' ? '' : Number(text)}
          onChange={(v) => onChange(v === '' ? null : v)}
        />
      );
    case 'date':
      return <TextInput {...common} type="date" value={text.slice(0, 10)} onChange={(e) => onChange(e.currentTarget.value || null)} />;
    case 'select':
      return (
        <Select
          {...common}
          value={text}
          onChange={(e) => onChange(e.currentTarget.value || null)}
          data={[{ value: '', label: t('tickets.field.choose') }, ...(field.options ?? []).map((o) => ({ value: o, label: o }))]}
        />
      );
    case 'multi_select': {
      const list = Array.isArray(value) ? value.map(String) : [];
      return (
        <Stack gap="xs" role="group" aria-label={field.label}>
          <Text role="inputLabel">
            {field.label}
            {field.required && <span className="rr-tickets__required" aria-hidden> *</span>}
          </Text>
          <Group gap="sm" wrap="wrap">
            {(field.options ?? []).map((o) => (
              <Checkbox
                key={o}
                label={o}
                checked={list.includes(o)}
                disabled={disabled}
                onChange={(on) => onChange(on ? [...list, o] : list.filter((x) => x !== o))}
              />
            ))}
          </Group>
          {error && <Text role="caption" c="var(--rr-color-error)">{error}</Text>}
        </Stack>
      );
    }
    case 'yes_no':
      return (
        <Select
          {...common}
          value={value === true ? 'true' : value === false ? 'false' : ''}
          onChange={(e) => onChange(e.currentTarget.value === '' ? null : e.currentTarget.value === 'true')}
          data={[
            { value: '', label: t('tickets.field.choose') },
            { value: 'true', label: t('common.yes') },
            { value: 'false', label: t('common.no') }
          ]}
        />
      );
    case 'address':
      return <Textarea {...common} value={text} minRows={2} maxLength={300} onChange={(e) => onChange(e.currentTarget.value)} />;
    default:
      return <TextInput {...common} value={text} maxLength={500} onChange={(e) => onChange(e.currentTarget.value)} />;
  }
}

/** A field's value for reading. */
export function fieldValueText(t: (k: string) => string, field: tickets.TypeField, value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  if (field.type === 'yes_no') return value === true ? t('common.yes') : value === false ? t('common.no') : String(value);
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}
